import React, { Fragment, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UncontrolledPopover } from 'reactstrap/lib';
import {
  PopoverBody, Row, Col, Button, Pagination, PaginationItem, PaginationLink,
  Label,
  Input,
} from 'reactstrap';
import { size } from 'lodash';
import { getUsersEmailChangesHistory } from '../../../actions/admin';
import DateFormat from '../UI/DateFormat';
import { FORMAT_DATE_TYPE } from '../../../constants/constants';


const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "verified", label: "Verified" },
  { value: "pending", label: "Pending" },
];

const SEARCH_BY_OPTIONS = [
  { value: "lowerCaseName", label: "Name" },
  { value: "lowerCaseLastName", label: "Last Name" },
  { value: "uid", label: "Player Id" },
  { value: "email", label: "Email" },
  { value: "domain", label: "Domain" },
];


const EmailChangeHistory = ({ offset }) => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(50);
  const [filterType, setFilterType] = useState(SEARCH_BY_OPTIONS[0].value);
  const [filter, setFilter] = useState('');
  const [filterStatus, setFilterStatus] = useState(STATUS_OPTIONS[0].value);

  const usersEmailChangesHistory = useSelector(state => state.admin.usersEmailChangesHistory || {});
  const loading = useSelector(state => state.admin.usersEmailChangesHistoryLoading || false);

  useEffect(() => {
    setCurrentPage(0);
  }, [filter, filterType]);


  const filteredHistory = useMemo(() => {
    if (!usersEmailChangesHistory) return {};

    const search = filter.toLowerCase();

    return Object.fromEntries(
      Object.entries(usersEmailChangesHistory).filter(([_, value]) => {
        if (filterStatus !== 'all' && value.status !== filterStatus) {
          return false;
        }

        if (filter === '') return true;

        if (filterType === 'lowerCaseName') {
          return value.playerName?.toLowerCase().startsWith(search);
        }

        if (filterType === 'lowerCaseLastName') {
          return value.lastName?.toLowerCase().startsWith(search);
        }

        if (filterType === 'uid') {
          return value.uid?.toLowerCase() === search;
        }

        if (filterType === 'email') {
          return (
            value.newEmail?.toLowerCase().startsWith(search) ||
            value.oldEmail?.toLowerCase().startsWith(search)
          );
        }

        if (filterType === 'domain') {
          const newDomain = value.newEmail?.split('@').pop()?.toLowerCase() || '';
          const oldDomain = value.oldEmail?.split('@').pop()?.toLowerCase() || '';
          return newDomain.startsWith(search) || oldDomain.startsWith(search);
        }

        return true;
      })
    );
  }, [usersEmailChangesHistory, filter, filterType, filterStatus]);



  // Helper function to safely render field values
  const safeRenderField = (value, fallback = 'N/A') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value.toString();
    // For objects or other types, return fallback
    return fallback;
  };

  const simplifyPlatform = (platform) => {
    if (['web', 'draugiem', 'inbox', 'facebook'].includes(platform)) {
      return 'web';
    }

    return platform;
  }

  useEffect(() => {
    dispatch(getUsersEmailChangesHistory());
  }, []);

  const handleClick = (e, index) => {
    e.preventDefault();
    setCurrentPage(index);
  };


  const pagesCount = Math.ceil(size(filteredHistory) / pageSize);

  console.log('usersEmailChangesHistory', usersEmailChangesHistory);


  return (
    <Fragment>
      <UncontrolledPopover popperClassName="popover" placement="right" trigger="focus" target="admin-help-button-email-change-history">
        <PopoverBody className="popover-body">
          A list of all users that have changed their email.
        </PopoverBody>
      </UncontrolledPopover>
      <div className="admin-page-content">
        <Row>
          <Col md="6">
            <h2>
              Email Change History
              <Button className="admin-help-button" id="admin-help-button-email-change-history">
                ?
              </Button>
            </h2>
          </Col>
        </Row>

        <Row>
          <Col sm="2">Search Players</Col>

          <Col sm="10">
            <Row>

              <Col sm="2">
                <Label for="filterType">
                  Search By
                </Label>
                <Input type="select" className="game-bet-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                  {SEARCH_BY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col sm="2">
                <Label for="filter">
                  Value
                </Label>
                <Input
                  type="text"
                  name="filter"
                  id="filter"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              </Col>

              <Col sm="2">
                <Label for="filterType">
                  Status
                </Label>
                <Input type="select" className="game-bet-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Input>
              </Col>
            </Row>
          </Col>

        </Row>


        {loading && <div>Loading...</div>}
        {!loading && size(filteredHistory) > 0 && (
          <Fragment>
            <table style={{ width: '100%', fontSize: 12 }}>
              <colgroup>
                <col span="1" className="" />
              </colgroup>
              <thead>
                <tr>
                  <th style={{ textAlign: 'center' }}>
                    Date
                  </th>
                  <th style={{ textAlign: 'center' }}>
                    Player ID
                  </th>
                  <th style={{ textAlign: 'center' }}>
                    Player Name
                  </th>
                  <th style={{ textAlign: 'center' }}>
                    Registration Method
                  </th>
                  <th style={{ textAlign: 'center' }}>
                    Platform
                  </th>
                  <th style={{ textAlign: 'center' }}>
                    Old Email
                  </th>
                  <th style={{ textAlign: 'center' }}>
                    New Email
                  </th>
                  <th style={{ textAlign: 'center' }}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory && Object.keys(filteredHistory)
                  .sort((a, b) => filteredHistory[b].date - filteredHistory[a].date)
                  .slice(
                    currentPage * pageSize,
                    (currentPage + 1) * pageSize,
                  )
                  .map((key, index) => (
                    <Fragment key={key}>
                      <tr key={key} className={`allUsers-table-row ${index % 2 === 0 ? ('odd') : ('even')}`}>
                        <td className="allUsers-table-col">
                          {usersEmailChangesHistory[key]?.date ? (
                            <DateFormat formatType={FORMAT_DATE_TYPE.fullDateMinute} date={new Date(usersEmailChangesHistory[key]?.date + offset).toString()} />
                          ) : ('-')}
                        </td>
                        <td className="allUsers-table-col">
                          {safeRenderField(usersEmailChangesHistory[key]?.uid)}
                        </td>
                        <td className="allUsers-table-col">
                          {safeRenderField(usersEmailChangesHistory[key]?.playerName)}
                        </td>
                        <td className="allUsers-table-col">
                          {safeRenderField(usersEmailChangesHistory[key]?.socProvider)}
                        </td>
                        <td className="allUsers-table-col">
                          {safeRenderField(simplifyPlatform(usersEmailChangesHistory[key]?.platform))}
                        </td>
                        <td className="allUsers-table-col">
                          {safeRenderField(usersEmailChangesHistory[key]?.oldEmail)}
                        </td>
                        <td className="allUsers-table-col">
                          {safeRenderField(usersEmailChangesHistory[key]?.newEmail)}
                        </td>
                        <td className="allUsers-table-col">
                          {safeRenderField(usersEmailChangesHistory[key]?.status)}
                        </td>
                      </tr>
                    </Fragment>
                  ))}
                {pagesCount && Object.keys(usersEmailChangesHistory).length > pageSize && (
                  <Pagination aria-label="Page navigation example">
                    <PaginationItem disabled={currentPage === 0}>
                      <PaginationLink style={{ color: '#000' }} onClick={e => handleClick(e, 0)} href="#">
                        ‹‹‹
                      </PaginationLink>
                    </PaginationItem>

                    <PaginationItem disabled={currentPage <= 0}>
                      <PaginationLink
                        style={{ color: '#000' }}
                        onClick={e => handleClick(e, currentPage - 1)}
                        previous
                        href="#"
                      />
                    </PaginationItem>

                    {[...Array(pagesCount)].map((page, i) => {
                      if (i > currentPage - 3 && i < currentPage + 3) {
                        return (
                          <PaginationItem active={i === currentPage} key={page}>
                            <PaginationLink style={{ color: '#000' }} onClick={e => handleClick(e, i)} href="#">
                              {i + 1}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      }

                      return null;
                    })}

                    <PaginationItem disabled={currentPage >= pagesCount - 1}>
                      <PaginationLink
                        style={{ color: '#000' }}
                        onClick={e => handleClick(e, currentPage + 1)}
                        next
                        href="#"
                      />
                    </PaginationItem>

                    <PaginationItem disabled={currentPage >= pagesCount - 1}>
                      <PaginationLink style={{ color: '#000' }} onClick={e => handleClick(e, pagesCount - 1)} href="#">
                        ›››
                      </PaginationLink>
                    </PaginationItem>
                  </Pagination>
                )}
              </tbody>
            </table>
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

export default EmailChangeHistory;
